class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.addEventListener('click', this.onDrawerClick.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  onDrawerClick(event) {
    const planToggle = event.target.closest('[data-cart-plan-toggle]');
    const planOption = event.target.closest('[data-cart-plan-option]');
    const subscribe = event.target.closest('[data-cart-subscribe]');
    const upsell = event.target.closest('[data-cart-upsell]');

    if (!planToggle && !event.target.closest('[data-cart-plan]')) {
      this.closePlanMenus();
    }

    if (planToggle) {
      event.preventDefault();
      const wrap = planToggle.closest('[data-cart-plan]');
      const menu = wrap.querySelector('[data-cart-plan-menu]');
      const expanded = planToggle.getAttribute('aria-expanded') === 'true';
      this.closePlanMenus();
      if (!expanded) {
        planToggle.setAttribute('aria-expanded', 'true');
        menu.hidden = false;
      }
      return;
    }

    if (planOption) {
      event.preventDefault();
      this.changeLine(planOption.dataset.line, planOption.dataset.quantity, planOption.dataset.planId);
      return;
    }

    if (subscribe) {
      event.preventDefault();
      this.changeLine(subscribe.dataset.line, subscribe.dataset.quantity, subscribe.dataset.planId);
      return;
    }

    if (upsell) {
      event.preventDefault();
      this.addUpsell(upsell.dataset.variantId, upsell);
    }
  }

  closePlanMenus() {
    this.querySelectorAll('[data-cart-plan-toggle]').forEach((button) => {
      button.setAttribute('aria-expanded', 'false');
      const menu = button.closest('[data-cart-plan]')?.querySelector('[data-cart-plan-menu]');
      if (menu) menu.hidden = true;
    });
  }

  changeLine(line, quantity, sellingPlan) {
    const sections = this.getSectionsToRender().map((section) => section.id);
    fetch(window.routes.cart_change_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        line: Number(line),
        quantity: Number(quantity),
        selling_plan: sellingPlan,
        sections,
        sections_url: window.location.pathname,
      }),
    })
      .then((response) => response.json())
      .then((state) => {
        if (state.status || state.errors) return;
        this.renderContents(state);
      })
      .catch((error) => {
        console.error(error);
      });
  }

  addUpsell(variantId, button) {
    if (!variantId || button.getAttribute('aria-disabled') === 'true') return;
    button.setAttribute('aria-disabled', 'true');

    const config = fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];

    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', '1');
    formData.append('sections', this.getSectionsToRender().map((section) => section.id));
    formData.append('sections_url', window.location.pathname);
    config.body = formData;

    fetch(window.routes.cart_add_url, config)
      .then((response) => response.json())
      .then((state) => {
        if (state.status) {
          button.removeAttribute('aria-disabled');
          return;
        }
        this.renderContents(state);
      })
      .catch((error) => {
        console.error(error);
        button.removeAttribute('aria-disabled');
      });
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.body.classList.add('overflow-hidden');

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
// Open the cart drawer when a page loads with ?cart=open (e.g. email links)
(function () {
  var params = new URLSearchParams(window.location.search);
  if (params.get('cart') !== 'open') return;
  function openDrawer() {
    var drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.open === 'function') drawer.open();
    params.delete('cart');
    var qs = params.toString();
    history.replaceState(null, '', location.pathname + (qs ? '?' + qs : '') + location.hash);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', openDrawer);
  else openDrawer();
})();